// ConfirmDialog.jsx
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

const FONT = '"DM Sans", sans-serif';

const Key = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      px: 0.75,
      py: '1px',
      borderRadius: '6px',
      border: '1px solid #D0D5DD',
      borderBottomWidth: '2px',
      bgcolor: '#FFFFFF',
      fontFamily: FONT,
      fontSize: '11px',
      fontWeight: 600,
      color: '#344054',
    }}
  >
    {children}
  </Box>
);

/**
 * Confirmation before sending a message: shows exactly what will be sent (line breaks included).
 * Enter confirms (the confirm button has the focus), Escape or ✕ cancels.
 * While `loading`, the dialog stays open and can't be dismissed.
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = "Confirmation",
  message = "Êtes-vous sûr ?",
  pendingMessage = null,
  confirmLabel = "Confirmer",
  loading = false,
}) {
  const handleClose = loading ? undefined : onClose;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={pendingMessage ? "sm" : "xs"}
      fullWidth
      aria-labelledby="confirm-dialog-title"
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(16, 24, 40, 0.45)', backdropFilter: 'blur(2px)' } } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, px: 3, pt: 3, pb: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: '#E8F1FC',
            color: '#1671D9',
            boxShadow: '0 0 0 6px #F3F8FE',
          }}
        >
          <SendRoundedIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          <Typography id="confirm-dialog-title" sx={{ fontFamily: FONT, fontSize: '18px', fontWeight: 600, color: '#101828', lineHeight: '26px' }}>
            {title}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: '14px', color: '#475467', lineHeight: '20px', mt: 0.25 }}>
            {message}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={loading} aria-label="Fermer" sx={{ color: '#98A2B3', mt: -0.5, mr: -1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Message preview */}
      {pendingMessage && (
        <Box sx={{ px: 3, pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#667085' }}>
              Aperçu du message
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#98A2B3' }}>
              {pendingMessage.length} caractère{pendingMessage.length > 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              maxHeight: '45vh',
              overflowY: 'auto',
              borderRadius: '14px',
              borderTopRightRadius: '4px',
              border: '1px solid #C9DEF8',
              bgcolor: '#E3EFFD',
              fontFamily: FONT,
              fontSize: '14px',
              lineHeight: '21px',
              color: '#101828',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {pendingMessage}
          </Box>
        </Box>
      )}

      {/* Actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: '1px solid #EAECF0',
          bgcolor: '#F9FAFB',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            gap: 0.75,
            fontFamily: FONT,
            fontSize: '12px',
            color: '#667085',
            whiteSpace: 'nowrap',
          }}
        >
          <Key>Entrée</Key> pour {confirmLabel.toLowerCase()}
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontFamily: FONT,
              fontWeight: 600,
              borderRadius: '8px',
              color: '#344054',
              borderColor: '#D0D5DD',
              bgcolor: '#FFFFFF',
              '&:hover': { borderColor: '#98A2B3', bgcolor: '#F9FAFB' },
            }}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={onConfirm}
            disabled={loading}
            autoFocus
            disableFocusRipple
            endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              fontFamily: FONT,
              fontWeight: 600,
              borderRadius: '8px',
              px: 2,
              bgcolor: '#1671D9',
              '&:hover': { bgcolor: '#125FB8' },
              '&.Mui-focusVisible': { boxShadow: '0 0 0 4px rgba(22, 113, 217, 0.24)' },
            }}
          >
            {loading ? 'Envoi…' : confirmLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
